import { useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Space,
  Tag,
  Typography,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  Alert,
  Spin,
  Divider,
  Select,
  Popconfirm,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useServiceRequestById,
  useAssignServiceRequest,
  useStartServiceRequest,
  useCompleteServiceRequest,
  useCloseServiceRequest,
  useAddServicePart,
  useRemoveServicePart,
  useServiceEngineers,
} from '@hooks/useService';
import type {
  ServiceRequestStatus,
  ServiceRequestType,
  ServicePriority,
  ServicePartDto,
} from '@/types/service';
import { downloadServiceActPdf } from '@/api/exportApi';

const MD_STATUS_COLOR_MAP: Record<ServiceRequestStatus, string> = {
  New: 'default',
  Assigned: 'processing',
  InProgress: 'blue',
  WaitingForParts: 'orange',
  Completed: 'green',
  Closed: 'default',
  Cancelled: 'red',
};

const MD_STATUS_LABEL_MAP: Record<ServiceRequestStatus, string> = {
  New: 'Нова',
  Assigned: 'Призначена',
  InProgress: 'В роботі',
  WaitingForParts: 'Очікує запчастини',
  Completed: 'Завершена',
  Closed: 'Закрита',
  Cancelled: 'Скасована',
};

const MD_PRIORITY_COLOR_MAP: Record<ServicePriority, string> = {
  Critical: 'red',
  High: 'orange',
  Normal: 'blue',
  Low: 'default',
};

const MD_PRIORITY_LABEL_MAP: Record<ServicePriority, string> = {
  Critical: 'Критичний',
  High: 'Високий',
  Normal: 'Нормальний',
  Low: 'Низький',
};

const MD_TYPE_LABEL_MAP: Record<ServiceRequestType, string> = {
  Warranty: 'Гарантійний',
  PostWarranty: 'Післягарантійний',
  Maintenance: 'Обслуговування',
  Installation: 'Встановлення',
  Calibration: 'Калібрування',
};

// Сервер приймає і знімає запчастини лише під час робіт
const MD_PARTS_EDITABLE_STATUSES: ServiceRequestStatus[] = ['InProgress', 'WaitingForParts'];

function mdGetSlaIndicatorColor(mdDeadline: string | null, mdBreached: boolean): string {
  if (mdBreached) return 'red';
  if (!mdDeadline) return 'default';
  const mdHoursLeft = dayjs(mdDeadline).diff(dayjs(), 'hour');
  if (mdHoursLeft < 4) return 'red';
  if (mdHoursLeft < 12) return 'orange';
  return 'green';
}

interface ServiceRequestDetailPageProps {
  requestId: string;
  onBack: () => void;
}

export default function ServiceRequestDetailPage({
  requestId,
  onBack,
}: ServiceRequestDetailPageProps) {
  const [mdPdfLoading, setMdPdfLoading] = useState(false);
  const [mdAssignModalOpen, setMdAssignModalOpen] = useState(false);

  const mdHandleDownloadPdf = async () => {
    setMdPdfLoading(true);
    try {
      await downloadServiceActPdf(requestId);
    } finally {
      setMdPdfLoading(false);
    }
  };
  const [mdCompleteModalOpen, setMdCompleteModalOpen] = useState(false);
  const [mdPartModalOpen, setMdPartModalOpen] = useState(false);
  const [mdAssignForm] = Form.useForm();
  const [mdCompleteForm] = Form.useForm();
  const [mdPartForm] = Form.useForm();

  const { data: mdDetail, isLoading: mdIsLoading } = useServiceRequestById(requestId);
  const mdAssignMutation = useAssignServiceRequest();
  const mdStartMutation = useStartServiceRequest();
  const mdCompleteMutation = useCompleteServiceRequest();
  const mdCloseMutation = useCloseServiceRequest();
  const mdAddPartMutation = useAddServicePart();
  const mdRemovePartMutation = useRemoveServicePart();
  // Перелік потрібен лише у формі призначення
  const { data: mdEngineers, isLoading: mdEngineersLoading } = useServiceEngineers();

  const mdHandleAssign = async () => {
    const mdValues = await mdAssignForm.validateFields();
    await mdAssignMutation.mutateAsync({ id: requestId, engineerId: mdValues.engineerId });
    setMdAssignModalOpen(false);
    mdAssignForm.resetFields();
  };

  const mdHandleStart = async () => {
    await mdStartMutation.mutateAsync(requestId);
  };

  const mdHandleComplete = async () => {
    const mdValues = await mdCompleteForm.validateFields();
    await mdCompleteMutation.mutateAsync({ id: requestId, resolution: mdValues.resolution });
    setMdCompleteModalOpen(false);
    mdCompleteForm.resetFields();
  };

  const mdHandleClose = async () => {
    await mdCloseMutation.mutateAsync(requestId);
  };

  const mdHandleAddPart = async () => {
    const mdValues = await mdPartForm.validateFields();
    await mdAddPartMutation.mutateAsync({
      requestId,
      part: {
        productId: mdValues.productId,
        quantity: mdValues.quantity,
        unitPrice: mdValues.unitPrice,
      },
    });
    setMdPartModalOpen(false);
    mdPartForm.resetFields();
  };

  if (mdIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!mdDetail) {
    return (
      <Alert type="error" message="Заявку не знайдено" showIcon />
    );
  }

  const mdSlaColor = mdGetSlaIndicatorColor(mdDetail.slaDeadline, mdDetail.isSlaBreached);
  const mdPartsEditable = MD_PARTS_EDITABLE_STATUSES.includes(mdDetail.status);

  const mdPartsColumns: ColumnsType<ServicePartDto> = [
    {
      title: 'Продукт',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Артикул',
      dataIndex: 'productSku',
      key: 'productSku',
      width: 140,
    },
    {
      title: 'К-ть',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right',
    },
    {
      title: 'Ціна',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Сума',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 120,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    ...(mdPartsEditable
      ? [{
        title: 'Дії',
        key: 'actions',
        width: 80,
        render: (_: unknown, mdRecord: ServicePartDto) => (
          <Popconfirm
            title="Видалити запчастину?"
            onConfirm={() => mdRemovePartMutation.mutate({ requestId, partId: mdRecord.id })}
            okText="Так"
            cancelText="Ні"
          >
            <Button type="link" size="small" danger>
              Видалити
            </Button>
          </Popconfirm>
        ),
      }]
      : []),
  ];

  const mdRenderActions = () => {
    const mdButtons: React.ReactNode[] = [];
    const mdStatus = mdDetail.status;

    if (mdStatus === 'New') {
      mdButtons.push(
        <Button
          key="assign"
          type="primary"
          onClick={() => setMdAssignModalOpen(true)}
        >
          Призначити інженера
        </Button>
      );
    }

    if (mdStatus === 'Assigned') {
      mdButtons.push(
        <Button key="start" type="primary" onClick={mdHandleStart} loading={mdStartMutation.isPending}>
          Почати роботу
        </Button>
      );
    }

    if (mdStatus === 'InProgress' || mdStatus === 'WaitingForParts') {
      mdButtons.push(
        <Button
          key="complete"
          type="primary"
          onClick={() => setMdCompleteModalOpen(true)}
        >
          Завершити
        </Button>
      );
    }

    if (mdStatus === 'Completed') {
      mdButtons.push(
        <Button key="close" type="primary" onClick={mdHandleClose} loading={mdCloseMutation.isPending}>
          Закрити
        </Button>
      );
    }

    return mdButtons;
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Назад до заявок
        </Button>
      </Space>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Заявка {mdDetail.requestNumber}
          </Typography.Title>
          <Space>
            <Tag color={MD_STATUS_COLOR_MAP[mdDetail.status]}>
              {MD_STATUS_LABEL_MAP[mdDetail.status]}
            </Tag>
            <Tag color={MD_PRIORITY_COLOR_MAP[mdDetail.priority]}>
              {MD_PRIORITY_LABEL_MAP[mdDetail.priority]}
            </Tag>
            {mdDetail.slaDeadline && (
              <Tag color={mdSlaColor}>
                SLA: {dayjs(mdDetail.slaDeadline).format('DD.MM.YYYY HH:mm')}
                {mdDetail.isSlaBreached && ' (ПОРУШЕНО)'}
              </Tag>
            )}
            <Button
              icon={<DownloadOutlined />}
              onClick={mdHandleDownloadPdf}
              loading={mdPdfLoading}
            >
              Завантажити акт PDF
            </Button>
          </Space>
        </div>

        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Тип">{MD_TYPE_LABEL_MAP[mdDetail.type] ?? mdDetail.type}</Descriptions.Item>
          <Descriptions.Item label="Клієнт">{mdDetail.customerName}</Descriptions.Item>
          <Descriptions.Item label="Обладнання">
            {mdDetail.equipmentSerialNumber}
            {mdDetail.equipmentProductName ? ` — ${mdDetail.equipmentProductName}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Інженер">
            {/* Облікового запису інженера вже немає — показуємо ідентифікатор */}
            {mdDetail.assignedEngineerName
              ?? (mdDetail.assignedEngineerId
                ? <Typography.Text type="secondary">{mdDetail.assignedEngineerId}</Typography.Text>
                : '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Створено">{dayjs(mdDetail.createdAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
          <Descriptions.Item label="Пріоритет">
            <Tag color={MD_PRIORITY_COLOR_MAP[mdDetail.priority]}>
              {MD_PRIORITY_LABEL_MAP[mdDetail.priority]}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {mdDetail.description && (
          <>
            <Divider orientation="left">Опис</Divider>
            <Typography.Paragraph>{mdDetail.description}</Typography.Paragraph>
          </>
        )}

        <Divider orientation="left">Використані запчастини</Divider>
        {mdPartsEditable && (
          <div style={{ marginBottom: 8 }}>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setMdPartModalOpen(true)}
            >
              Додати запчастину
            </Button>
          </div>
        )}
        <Table<ServicePartDto>
          columns={mdPartsColumns}
          dataSource={mdDetail.partsUsed}
          rowKey="id"
          pagination={false}
          size="small"
        />

        {(mdDetail.status === 'Completed' || mdDetail.status === 'Closed') && mdDetail.resolution && (
          <>
            <Divider orientation="left">Результат</Divider>
            <Typography.Paragraph>{mdDetail.resolution}</Typography.Paragraph>
          </>
        )}

        <Divider />
        <Space>{mdRenderActions()}</Space>
      </Card>

      {/* Assign engineer modal */}
      <Modal
        title="Призначити інженера"
        open={mdAssignModalOpen}
        onOk={mdHandleAssign}
        onCancel={() => setMdAssignModalOpen(false)}
        confirmLoading={mdAssignMutation.isPending}
        okText="Призначити"
        cancelText="Скасувати"
      >
        <Form form={mdAssignForm} layout="vertical">
          {/* Сервер приймає лише активного користувача ролі ServiceEngineer */}
          <Form.Item
            name="engineerId"
            label="Інженер"
            rules={[{ required: true, message: 'Оберіть інженера' }]}
          >
            <Select
              placeholder="Оберіть інженера"
              showSearch
              optionFilterProp="label"
              loading={mdEngineersLoading}
              notFoundContent="Немає активних сервісних інженерів"
              options={(mdEngineers ?? []).map((mdEngineer) => ({ value: mdEngineer.id, label: mdEngineer.fullName }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Complete request modal */}
      <Modal
        title="Завершити заявку"
        open={mdCompleteModalOpen}
        onOk={mdHandleComplete}
        onCancel={() => setMdCompleteModalOpen(false)}
        confirmLoading={mdCompleteMutation.isPending}
        okText="Завершити"
        cancelText="Скасувати"
      >
        <Form form={mdCompleteForm} layout="vertical">
          <Form.Item
            name="resolution"
            label="Результат роботи"
            rules={[{ required: true, message: 'Опишіть результат' }]}
          >
            <Input.TextArea rows={4} placeholder="Опишіть виконану роботу..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add part modal */}
      <Modal
        title="Додати запчастину"
        open={mdPartModalOpen}
        onOk={mdHandleAddPart}
        onCancel={() => setMdPartModalOpen(false)}
        confirmLoading={mdAddPartMutation.isPending}
        okText="Додати"
        cancelText="Скасувати"
      >
        <Form form={mdPartForm} layout="vertical">
          <Form.Item
            name="productId"
            label="Продукт (ID)"
            rules={[{ required: true, message: 'Введіть ID продукту' }]}
          >
            <Input placeholder="ID продукту" />
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Кількість"
            rules={[{ required: true, message: 'Введіть кількість' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="unitPrice"
            label="Ціна за одиницю"
            rules={[{ required: true, message: 'Введіть ціну' }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
